import "./interfaces.d";

import { observable, action, computed, makeObservable, runInAction } from "mobx";
import { API } from "General/API";
const AUTH_STORAGE = "AuthenticationStore";
const TOKEN_TIMEOUT = "TokenTimeout";
const GUEST_TOKEN = "AccessId";
import i18n from "i18next";
import { dayjs } from "Config/dayjs";
import { BrowserStorage } from "utils/BrowserStorage";
import { notificationWarning, notificationSuccess } from "utils/libHelpers/AntdNotification";
import { clearAllStores } from "./RESTStore/CoreStore";
import { I18N_NS } from "Config/i18n";
import { API_URL } from "General/APIEndpoints";

const USER_SORTING_KEY = "user_sorting";

/**
 * Authentication
 *
 * Allows Global Control over the status of Authentication
 *
 */
class UserInfoStore {
  loginInfo: API_LoginInformation = {};
  guestToken: string = null;
  initStatusCheckComplete = false;
  private logoutInfo: API_LoginInformation = {
    status: null,
    id: undefined,
    name: "",
    token: "",
  };

  constructor() {
    makeObservable(this, {
      loginInfo: observable,
      guestToken: observable,
      initStatusCheckComplete: observable,
      authenticate: action.bound,
      replaceJWT: action.bound,
      signOut: action.bound,
      setGuestToken: action.bound,
      isAuthenticated: computed,
      status: computed,
      userID: computed,
      id: computed,
      name: computed,
      allowedToChangePassword: computed,
      dateFormat: computed,
      allowedToChangeSetting: computed,
      role: computed,
      setLanguage: action,
      initStatusChecker: action.bound,
      isAdmin: computed,
      isSysAdmin: computed,
      language: computed,
      isUnifiedUser: computed,
    });

    this.loginInfo =
      BrowserStorage.storePermanent(AUTH_STORAGE) || BrowserStorage.store(AUTH_STORAGE) || this.logoutInfo;
    this.guestToken = BrowserStorage.store(GUEST_TOKEN) || undefined;
  }

  authenticate(
    data: API_LoginInformation,
    { isGuest = false, rewrite = false }: { isGuest?: boolean; rewrite?: boolean } = {}
  ) {
    if (rewrite) {
      this.loginInfo = { ...data };
    } else {
      this.loginInfo = { ...this.loginInfo, ...data };
    }

    let storeFn: (namespace: string, data?: object | string | number | null) => void;
    if (isGuest) {
      storeFn = BrowserStorage.store;
    } else {
      storeFn = BrowserStorage.storePermanent;
    }
    if (data.rememberMeExpiresAt) {
      notificationSuccess(
        i18n.t("Note", { ns: I18N_NS.LOGIN }),
        i18n.t("You will stay logged in until {{time}}", {
          ns: I18N_NS.LOGIN,
          time: dayjs(data.rememberMeExpiresAt).format("lll"),
        })
      );
    }
    if (data.badLoginSinceLastLogin === true) {
      if (this.loginInfo.language.toLowerCase() === "de") {
        notificationWarning(
          `Warnung`,
          `Seit Ihrer letzten Anmeldung wurde(n) ${data.badLoginCount}
                  fehlgeschlagene(r) Loginversuche registiert.`
        );
      } else {
        notificationWarning(`Warning`, `Since your last Login there were ${data.badLoginCount} bad login requests.`);
      }
      this.loginInfo.badLoginSinceLastLogin = false;
      this.loginInfo.badLoginCount = 0;
      this.loginInfo.badLoginIP = null;
      this.loginInfo.badLoginTime = null;
    }
    if (data.status === null) {
      storeFn(AUTH_STORAGE, null);
      storeFn(TOKEN_TIMEOUT, null);
      storeFn(GUEST_TOKEN, null);
    } else if (data.status === "OK" || data.status === "MCP_WRITE_CONSENT") {
      storeFn(AUTH_STORAGE, this.loginInfo);
      // Automatic SignOut After 30min
      storeFn(TOKEN_TIMEOUT, dayjs().add(30, "m"));

      // Pave-612
      // Keep the local selected language and not change it to a new value from the BE (just for guests)
      if (data.role !== "GUEST" && data.language) {
        const newLanguage = data.language.toLowerCase();
        i18n.changeLanguage(newLanguage);
        dayjs.locale(newLanguage === "en" ? "en-gb" : newLanguage);
      }
    }
  }

  replaceJWT(token: string) {
    this.loginInfo.jwtToken = token;
  }

  signOut(from: string, silent = true) {
    console.warn("signOut WAS CALLED from: ", from);

    if (!silent) {
      API.logout();
    }
    this.authenticate(this.logoutInfo, { rewrite: true });

    window.sessionStorage.clear();
    clearAllStores();
  }

  async initStatusChecker() {
    try {
      await this.fetchStatus();
      setInterval(() => this.fetchStatus(), 60000);
    } catch (error) {
      throw error;
    } finally {
      runInAction(() => {
        this.initStatusCheckComplete = true;
      });
    }
  }

  async fetchStatus() {
    try {
      if (this.isAuthenticated) {
        const data: API_LoginInformation = await API.getData(API_URL.LOGIN.getStatus());
        if (data.role !== this.role || data.status !== this.status || data.name !== this.name) {
          this.authenticate(data);
        }
      }
    } catch (error) {
      throw error;
    }
  }

  setGuestToken(guestToken: string) {
    BrowserStorage.store(GUEST_TOKEN, guestToken);
    this.guestToken = guestToken;
  }

  /**
   * Getter for Authentication Status
   */
  get isAuthenticated(): boolean {
    return this.loginInfo.status === "OK";
  }

  get status() {
    return this.loginInfo.status || null;
  }

  public get userID(): string {
    return this.loginInfo.userId;
  }

  public get id(): number {
    return +this.loginInfo.id;
  }

  public get allowedToChangePassword(): boolean {
    return this.loginInfo.allowedToChangePassword;
  }

  get dateFormat() {
    return this.loginInfo.language.toLowerCase() === "de" ? "DD-MM-YYYY" : "YYYY-MM-DD";
  }

  public get allowedToChangeSetting(): boolean {
    return this.loginInfo.allowedToChangeSetting;
  }

  public get isRestrictedUser(): boolean {
    return !this.allowedToChangeSetting;
  }

  public get showNewsletter(): boolean {
    return this.loginInfo.showNewsletter;
  }

  public get name(): string {
    return this.loginInfo.name;
  }

  public get csrf(): string {
    return this.loginInfo.token;
  }

  public get jwtToken(): string {
    return this.loginInfo.jwtToken;
  }

  public get role() {
    if (this.loginInfo.role === "UNKNOWN") {
      return "USER";
    } else {
      return this.loginInfo.role;
    }
  }

  public get language() {
    return this.loginInfo?.language?.toLowerCase();
  }

  public setLanguage(lang: string) {
    this.loginInfo.language = lang.toLowerCase();
    BrowserStorage.storePermanent(AUTH_STORAGE, this.loginInfo);
    // location.reload();
  }

  getSortingSettings(namespace: string): { direction: "asc" | "desc"; sortBy: string } | null {
    const settings = BrowserStorage.getPermanent(`${USER_SORTING_KEY}_${this.id}`);
    if (settings) {
      return settings[namespace] || null;
    }
    return null;
  }

  getSortingSettingsForAntdTable(namespace: string): { order: "ascend" | "descend"; columnKey: string } | null {
    const settings = this.getSortingSettings(namespace);

    if (settings) {
      return { order: settings.direction === "asc" ? "ascend" : "descend", columnKey: settings.sortBy };
    }
    return null;
  }

  storeSortingSettings(namespace: string, sorting: { direction: "asc" | "desc"; sortBy: string }) {
    let settings = BrowserStorage.getPermanent(`${USER_SORTING_KEY}_${this.id}`);
    if (settings) {
      settings[namespace] = sorting;
    } else {
      settings = { [namespace]: sorting };
    }
    BrowserStorage.storePermanent(`${USER_SORTING_KEY}_${this.id}`, settings);
  }

  public get tokenType() {
    const { tokenType } = this.loginInfo;
    return tokenType ? tokenType : null;
  }

  public isVisibleFor(roles: Roles | Array<Roles>): boolean {
    roles = [].concat(roles);
    return roles.includes("ALL") || roles.includes(this.role) || this.role === "ADMIN";
  }

  get isAdmin() {
    return this.role === "ADMIN" || this.role === "PRGROUPADMIN";
  }

  get isSysAdmin() {
    return this.role === "ADMIN";
  }

  get isGuest() {
    return !this.role ? true : this.role === "GUEST" || this.role === "BIDDER";
  }

  get isUnifiedUser() {
    return this.loginInfo.unifiedAccount;
  }
}

const userInfoStore = new UserInfoStore();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).userInfo = userInfoStore;

export { UserInfoStore, userInfoStore };
